import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import usePatientDetailForProvider from "hooks/usePatientDetailForProvider";
import { PatientsPaginationRequestDtoStatusEnum } from "libs/cellera-sdk";
import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

import { practicePatientManagementApi } from "../../../apis";
import CancelPatientRequestModal from "./CancelPatientRequestModal";
import PatientDetail from "./detail/PatientDetail";
import { transformPatientToDetailData } from "./mappers/patientMapper";
import RequestPatientConfirmModal from "./RequestPatientConfirmModal";

const PatientDashboard: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();

  const handleBack = () => navigate(EPath.PATIENT_MANAGEMENT);

  const [refreshKey, setRefreshKey] = useState(0);
  const [isCancelRequestModalOpen, setIsCancelRequestModalOpen] =
    useState(false);
  const [isRequestConfirmModalOpen, setIsRequestConfirmModalOpen] =
    useState(false);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    refetch();
  };

  // Use the patient detail hook to fetch patient data
  const { patient, hasRequestedSuccessfullyBefore, loading, error, refetch } =
    usePatientDetailForProvider(patientId || "");

  const breadcrumbs = [
    { label: "Dashboard", href: "/dashboard", order: 1 },
    { label: "Patients", href: EPath.PATIENT_MANAGEMENT, order: 2 },
  ];

  // Transform patient data for PatientDetail component
  const patientDetailData = patient
    ? transformPatientToDetailData(patient)
    : null;

  const handleCancelRequest = useCallback(() => {
    setIsCancelRequestModalOpen(true);
  }, []);

  const handleCancelRequestSubmit = useCallback(async () => {
    if (!patient?.patientId) return;

    await practicePatientManagementApi.cancelDataSharingRequests({
      cancelDataSharingRequestsDto: {
        patientIds: [patient.patientId],
      },
    });

    // If patient has never shared data successfully before, navigate back to patient management
    if (!hasRequestedSuccessfullyBefore) {
      navigate(EPath.PATIENT_MANAGEMENT);
    } else {
      refetch();
    }
  }, [patient, hasRequestedSuccessfullyBefore, refetch, navigate]);

  const handleRequest = useCallback(() => {
    setIsRequestConfirmModalOpen(true);
  }, []);

  const handleRequestSubmit = useCallback(async () => {
    if (!patient?.email) return;

    await practicePatientManagementApi.requestPatientDataSharing({
      requestPatientDataSharingDto: {
        email: patient.email,
      },
    });

    refetch();
  }, [patient, refetch]);

  // Show loading state
  if (loading) {
    return (
      <DashboardLayout title="Patient Details">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2, color: colors.text.secondary }}>
            Loading patient details...
          </Typography>
        </Box>
      </DashboardLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <DashboardLayout title="Patient Details">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={refetch}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  // No patient found
  if (!patientDetailData) {
    return (
      <DashboardLayout title="Patient Details">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Typography variant="h6" sx={{ color: colors.text.secondary }}>
            Patient not found
          </Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Patient Details"
      breadcrumbs={breadcrumbs}
      lastBreadcrumb="Patient Details"
      onBack={handleBack}
      onRefresh={handleRefresh}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {/* Patient Detail Component - always show, content varies based on hasRequestedSuccessfullyBefore */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PatientDetail
            key={refreshKey}
            patient={patientDetailData}
            hasRequestedSuccessfullyBefore={hasRequestedSuccessfullyBefore}
            onViewAllAppointments={() => console.log("View all appointments")}
            onViewVitalHistory={() =>
              navigate(
                EPath.HEART_METRICS.replace(":patientId", patientId || "")
              )
            }
            onViewActivities={() =>
              navigate(EPath.ACTIVITY.replace(":patientId", patientId || ""))
            }
            onViewNutritionHistory={() =>
              navigate(
                EPath.NUTRITION_HYDRATION.replace(":patientId", patientId || "")
              )
            }
            onViewSleepHistory={() =>
              navigate(EPath.SLEEP.replace(":patientId", patientId || ""))
            }
            onViewBodyMeasurements={() =>
              navigate(
                EPath.BODY_MEASUREMENTS.replace(":patientId", patientId || "")
              )
            }
            onCancelRequest={
              patient?.status ===
              PatientsPaginationRequestDtoStatusEnum.Requesting
                ? handleCancelRequest
                : undefined
            }
            onRequest={
              patient?.status === PatientsPaginationRequestDtoStatusEnum.Revoked
                ? handleRequest
                : undefined
            }
          />
        </Box>
      </Box>

      {/* Cancel Patient Request Modal */}
      <CancelPatientRequestModal
        open={isCancelRequestModalOpen}
        onClose={() => setIsCancelRequestModalOpen(false)}
        onSubmit={handleCancelRequestSubmit}
        patientEmail={patient?.email}
      />

      {/* Request Patient Confirm Modal */}
      <RequestPatientConfirmModal
        open={isRequestConfirmModalOpen}
        onClose={() => setIsRequestConfirmModalOpen(false)}
        onSubmit={handleRequestSubmit}
        patientEmail={patient?.email}
      />
    </DashboardLayout>
  );
};

export default PatientDashboard;
